// @ts-strict-ignore
import { ApplicationRef, ComponentFactoryResolver, ComponentRef, EmbeddedViewRef, Injectable, Injector } from '@angular/core';
import { OverlayComponent } from './overlay.component';
import { OverlayBackground, OverlayType } from './overlay.type';
import { Subject } from 'rxjs';
import { UiUtils } from '../util/ui-utils';

@Injectable({ providedIn: 'root' })
export class OverlayService {
  public isOverlayRemovable: boolean = true;
  private isHideAll: boolean = false;
  private _colorOverlayRequestCount;
  private _transparentOverlayRequestCount;

  private colorOverlayComponent: ComponentRef<OverlayComponent>;
  private transparentOverlayComponent: ComponentRef<OverlayComponent>;

  public stateChange =  new Subject<boolean>();

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private applicationRef: ApplicationRef,
    private injector: Injector
  ) {
    this.init();
  }

  private init() {
    const overlayComponentFactory = this.componentFactoryResolver
      .resolveComponentFactory(OverlayComponent);

    this.colorOverlayComponent = overlayComponentFactory.create(this.injector);
    this.transparentOverlayComponent = overlayComponentFactory.create(this.injector);
    this.colorOverlayComponent.instance.opacity = 0.05;
    this.transparentOverlayComponent.instance.opacity = 0;

    this._colorOverlayRequestCount = 0;
    this._transparentOverlayRequestCount = 0;

    this.updateOverlayVisibility(this._colorOverlayRequestCount, this.colorOverlayComponent);
    this.updateOverlayVisibility(this._transparentOverlayRequestCount, this.transparentOverlayComponent);

    this.applicationRef.attachView(this.colorOverlayComponent.hostView);
    this.applicationRef.attachView(this.transparentOverlayComponent.hostView);

    const rootNodes = (this.colorOverlayComponent.hostView as EmbeddedViewRef<any>).rootNodes;
    const rootNodesForTransparent = (this.transparentOverlayComponent.hostView as EmbeddedViewRef<any>).rootNodes;
    const domElem = rootNodes[0] as HTMLElement;
    const domElemTransparent = rootNodesForTransparent[0] as HTMLElement;
    document.body.appendChild(domElem);
    document.body.appendChild(domElemTransparent);
    domElemTransparent.remove()
  }

  public getCurrentState(): boolean {
    return this._colorOverlayRequestCount || this._transparentOverlayRequestCount;
  }

  public async doWithOverlay<T>(asyncFunc: () => Promise<T>, overlayType: OverlayType = OverlayBackground.color): Promise<T> {
    this.requestOverlayShow(overlayType);
    // Let the UI establish the overlay, otherwise CPU intensive tasks might block the rendering
    // Transition is about 300ms, so wait a bit longer
    await UiUtils.doEvents(500);
    try {
      return await asyncFunc();
    } finally {
      this.requestOverlayHide(overlayType);
    }
  }

  public requestOverlayShow(overlayType: OverlayType, showImmediately: boolean = false) {
    if (overlayType === OverlayBackground.color) {
      this.requestShowColor(showImmediately);
    } else if (overlayType === OverlayBackground.transparent) {
      this.requestShowTransparent(showImmediately);
    }
  }

  public requestOverlayHide(overlayType: OverlayType, hideImmediately: boolean = false) {
    if (overlayType === OverlayBackground.color) {
      this.requestHideColor(hideImmediately);
    } else if (overlayType === OverlayBackground.transparent) {
      this.requestHideTransparent(hideImmediately);
    }
  }

  private requestShowColor(showImmediately: boolean = false) {
    this._colorOverlayRequestCount++;
    this.updateOverlayVisibility(this._colorOverlayRequestCount, this.colorOverlayComponent, showImmediately);
  }

  private requestShowTransparent(showImmediately: boolean = false) {
    this._transparentOverlayRequestCount++;
    this.updateOverlayVisibility(this._transparentOverlayRequestCount, this.transparentOverlayComponent, showImmediately);
  }

  private requestHideColor(hideImmediately: boolean = false) {
    if (this._colorOverlayRequestCount === 0) {
      return;
    }
    this._colorOverlayRequestCount--;
    this.updateOverlayVisibility(this._colorOverlayRequestCount, this.colorOverlayComponent, hideImmediately);
  }

  private requestHideTransparent(hideImmediately: boolean = false) {
    if (this._transparentOverlayRequestCount === 0) {
      return;
    }
    this._transparentOverlayRequestCount--;
    this.updateOverlayVisibility(this._transparentOverlayRequestCount, this.transparentOverlayComponent, hideImmediately);
  }

  private updateOverlayVisibility(count: number, component: ComponentRef<OverlayComponent>, showHideImmediately: boolean = false) {
    if (showHideImmediately) {
      this.updateOverlayVisibilityFunction(count, component);
    } else {
      this.isHideAll = false;
        if (!this.isHideAll) {
          this.updateOverlayVisibilityFunction(count, component);
        }
    }
  }

  private updateOverlayVisibilityFunction(count: number, component: ComponentRef<OverlayComponent>): void {
    setTimeout(() => {

    const isRemovable: boolean = this.isOverlayRemovable;
    if (count) {
      component.location.nativeElement.classList.add("loader-visible");
    } else {
      if (isRemovable) {
        component.location.nativeElement.classList.remove("loader-visible");
      }
    }
    this.stateChange.next(this.getCurrentState());
    }, 10)
  }

  public hideAllOverlays(): void {
    this.isOverlayRemovable = true;
    this.isHideAll = true;

    this.updateOverlayVisibilityFunction(0, this.transparentOverlayComponent);
    this._transparentOverlayRequestCount = 0;

    this.updateOverlayVisibilityFunction(0, this.colorOverlayComponent);
    this._colorOverlayRequestCount = 0;

  }
}
